
import { defineComponent, PropType } from 'vue';
import { mapGetters } from 'vuex';
import { isEqual } from 'lodash';
import { isNullOrUndefined, trailerType } from '@/shared/helpers';
import { ActionTypes } from '@/store/actionTypes';
import NearbyTrailerResult from '@/types/NearbyTrailerResult';
import Trailer from '../../types/Trailer';
import AlreadyHaveTrailerAttached from '@/components/AlreadyHaveTrailerAttached.vue';
import AttachTrailerConfirmation from '@/components/AttachTrailerConfirmation.vue';
import CompanyLogo from '@/components/CompanyLogo.vue';
import FbxButton from '@/components/fbx-button.vue';
import ItemNavigator from '@/components/ItemNavigator.vue';
import checkTrailerAttachability from '@/shared/mixins/checkTrailerAttachability';
import getTrailersAlreadyAttachedToTruck from '@/shared/mixins/getTrailersAlreadyAttachedToTruck';
import FbaApiError from '@/types/FbaApiError';
import DriverLoaded from '@/components/DriverLoaded.vue';
import { CargoStatus } from '@/types/CargoStatus';
import { TrailerSelectionMethod } from '@/types/TrailerSelectionMethod';

export default defineComponent({
    name: 'SelectedLocationToAttach',
    components: {
        AlreadyHaveTrailerAttached,
        AttachTrailerConfirmation,
        CompanyLogo,
        FbxButton,
        ItemNavigator,
        DriverLoaded,
    },
    mixins: [checkTrailerAttachability, getTrailersAlreadyAttachedToTruck],
    props: {
        isOverlayMinimized: {
            type: Boolean,
        },
        selectedResult: {
            type: Object as PropType<NearbyTrailerResult | null>,
            required: true,
        },
        small: {
            type: Boolean,
        },
        results: {
            type: Array as PropType<NearbyTrailerResult[]>,
            required: true,
        },
    },
    data(): {
        currentAttachedTrailers: Array<Trailer>;
        didSelectNavDestination: boolean;
        displayedResult: NearbyTrailerResult | null;
        showConfirm: boolean;
        detachModalHidden: boolean;
        showLoadedTrailerModal: boolean;
    } {
        return {
            currentAttachedTrailers: [],
            didSelectNavDestination: false,
            displayedResult: this.selectedResult,
            showConfirm: false,
            detachModalHidden: false,
            showLoadedTrailerModal: false,
        };
    },
    computed: {
        ...mapGetters({
            driver: 'getDriver',
            trailerAssociationsEnabled: 'trailerAssociationsEnabled',
        }),
        alreadyAttached(): boolean {
            return (
                !isNullOrUndefined(this.currentAttachedTrailers) &&
                this.currentAttachedTrailers.length > 0
            );
        },
        currentIndex() {
            return this.results.findIndex((result) =>
                isEqual(result, this.displayedResult)
            );
        },
        trailerName(): string {
            return `${this.isReserved ? 'Your ' : ''}Trailer #${
                this.trailerNumber
            }`;
        },
        hasAnyFault() {
            return (
                this.hasDotExpired === true || this.hasCargoSensorError === true
            );
        },
        hasDotExpired(): boolean {
            return (
                this.displayedResult?.trailers[0].isDotInspectionExpired ===
                true
            );
        },
        hasCargoSensorError(): boolean {
            return (
                this.displayedResult?.trailers[0].cargoStatus ===
                    CargoStatus.SENSOR_ERROR ||
                this.displayedResult?.trailers[0].cargoStatus ===
                    CargoStatus.NO_SENSOR
            );
        },
        hasCargoSensor(): boolean {
            return (
                this.displayedResult?.trailers[0].cargoStatus !==
                CargoStatus.NO_SENSOR
            );
        },
        lastUpdated(): string | undefined {
            if (isNullOrUndefined(this.displayedResult))
                return 'Last update unknown';

            const age = this.displayedResult?.trailers[0].sourceLastReported;
            if (age === null || age === undefined) return 'Last update unknown';
            else {
                const timestamp = age.toRelative({
                    unit: ['days', 'hours', 'minutes'],
                });
                const value = timestamp
                    ?.split(' ')
                    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                    .join(' ');
                return `Updated ${value}`;
            }
        },
        trailerTypeText(): string {
            if (isNullOrUndefined(this.displayedResult))
                return 'Trailer type unknown';
            return trailerType(this.displayedResult.trailers[0]);
        },
        isReserved(): boolean {
            if (!(this.displayedResult instanceof NearbyTrailerResult))
                return false;

            return (
                this.displayedResult.trailers.filter(
                    (x) => x.reservation?.isActive
                ).length > 0
            );
        },
        reservationEndTime(): string | undefined {
            if (this.isReserved) {
                return this.displayedResult?.trailers[0].reservation?.expiresAt
                    .toLocal()
                    .toFormat('hh:mma')
                    .toLowerCase();
            } else {
                return undefined;
            }
        },
        timeZone(): string | undefined {
            return new Date()
                .toLocaleTimeString('en-us', { timeZoneName: 'short' })
                .split(' ')[2];
        },
        trailerNumber(): string {
            return this.displayedResult?.trailers[0]?.trailerNumber || '';
        },
        trailerCompany(): string {
            return this.displayedResult?.trailers[0]?.company || '';
        },
        error(): FbaApiError | undefined {
            return this.$store.getters.getError;
        },
    },
    methods: {
        cancelConfirm(): void {
            this.showConfirm = false;
        },
        hideAttachModal() {
            this.detachModalHidden = true;
        },
        hideLoadedModal() {
            this.showLoadedTrailerModal = false;
        },
        attachTrailer(): void {
            const trailer = this.selectedResult?.trailers[0];

            if (!isNullOrUndefined(trailer) && trailer instanceof Trailer) {
                this.$store.dispatch(ActionTypes.SET_SELECTED_TRAILER, {
                    trailer,
                    trailerSelectionMethod: TrailerSelectionMethod.select,
                });
                this.$router.push({
                    name: 'TrailerAssessment',
                    params: { trailerNumber: trailer.trailerNumber },
                });
            }
        },
        backToListView(): void {
            this.$emit('backToListView');
        },
        cycleLeft(): void {
            let newIndex = this.currentIndex - 1;
            if (newIndex < 0) newIndex = this.results.length - 1;
            this.displayedResult = this.results[newIndex];
            this.$emit('setSelectedResult', this.displayedResult);
        },
        cycleRight(): void {
            const newIndex = (this.currentIndex + 1) % this.results.length;
            this.displayedResult = this.results[newIndex];
            this.$emit('setSelectedResult', this.displayedResult);
        },
        switchExpand(): void {
            this.$emit('resetOverlayHeight');
        },
        detachErrorOccurred() {
            this.currentAttachedTrailers = [];
            this.showConfirm = false;
        },
        async confirmTrailerAttach(detachSucceeded: boolean): Promise<void> {
            if (detachSucceeded === false) {
                this.detachErrorOccurred();
                return;
            }
            await this.confirmTrailerResult();
        },
        async confirmTrailerResult(): Promise<void> {
            const isTrailerAvailable = await this.checkTrailerAttachability({
                trailerNumber: this.trailerNumber,
                trailerCompany: this.trailerCompany,
                driverId: this.driver.id,
            });

            const attachedTrailers =
                await this.getTrailersAlreadyAttachedToTruck();

            if (
                !isNullOrUndefined(attachedTrailers) &&
                Array.isArray(attachedTrailers) &&
                !this.trailerAndDriverAreLoaded
            ) {
                this.currentAttachedTrailers = attachedTrailers;
                return;
            }

            if (this.trailerAndDriverAreLoaded === true) {
                this.hideAttachModal();
                if (!isNullOrUndefined(attachedTrailers)) {
                    this.currentAttachedTrailers = attachedTrailers;
                }

                this.showLoadedTrailerModal = true;
                return;
            }

            if (isTrailerAvailable === false) {
                this.$router.push({
                    name: 'TrailerUnavailable',
                    query: {
                        trailerNumber: this.trailerNumber,
                        trailerCompany: this.trailerCompany,
                    },
                });
                return;
            }

            this.currentAttachedTrailers = [];
            this.showConfirm = true;
        },
    },
    watch: {
        selectedResult(): void {
            this.displayedResult = this.selectedResult;
        },
    },
});
